import React, { useState, useEffect, useMemo } from "react";
import { DataGrid, GridColumnMenu } from "@mui/x-data-grid";
import { Button, Toolbar, TextField, InputAdornment, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useGridApiContext, useGridRootProps } from "@mui/x-data-grid";

// Heuristic to detect date-like columns and ensure proper sorting
const isDateLikeField = (field = "", type) => {
	if (type === "date" || type === "dateTime") return true;
  const f = String(field).toLowerCase();
	return f.includes("date") || f.endsWith("_at") || f.endsWith("_on");
};

const dateSortComparator = (v1, v2) => {
  const t1 = v1 ? new Date(v1).getTime() : 0;
  const t2 = v2 ? new Date(v2).getTime() : 0;
  return t1 - t2;
};

const Datagrid = ({
	data,
	columns,
	onSelect,
	customButtons,
	onPageChange,
	rowIdField,
	sortBy,
	pageSize = 50,
	showSearchBar = true,
	checkboxSelection = false,
	checkboxSelectionVisibleOnly = false,
	rowClick = false,
	onRowClick,
	pageSizeOptions,
	disableSelectAll = false,
	rowSelectionModel,
	keepNonExistentSelected = false,
	autoHeight = true,
	gridHeight = 520,
	handleSearch,
	extraFilters = null,
	hideFilterFromColumnMenu = false,
	lockActiveColumnInManageColumns = false,
	preventHidingLastColumn = false,
	hideFooterSelectedRowCount = false,
	footerSelectedRowText = "",
}) => {
	const [searchText, setSearchText] = useState("");
	const [filteredData, setFilteredData] = useState(data);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [activeMenuColumnField, setActiveMenuColumnField] = useState(null);
	const { t } = useTranslation();
	const normalizedPageSize = Number.isFinite(Number(pageSize)) ? Number(pageSize) : 10;
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: normalizedPageSize });
	const normalizedPageSizeOptions = useMemo(() => {
		const fallback = [5, 10, 25, 50];
		const base = Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0 ? pageSizeOptions : fallback;
		const options = new Set(base.map((val) => Number(val)).filter((val) => Number.isFinite(val) && val > 0));
		options.add(normalizedPageSize);
		return Array.from(options).sort((a, b) => a - b);
	}, [pageSizeOptions, normalizedPageSize]);
	const currentPageIds = useMemo(() => {
		const page = Number(paginationModel?.page || 0);
		const pageSizeValue = Number(paginationModel?.pageSize || normalizedPageSize);
		const start = page * pageSizeValue;
		const end = start + pageSizeValue;
		return (filteredData || []).slice(start, end).map((row) => row?.[rowIdField]);
	}, [filteredData, paginationModel, normalizedPageSize, rowIdField]);
	
	const rowIdSetKey = useMemo(
		() => (filteredData || []).map((row) => row?.[rowIdField]).join("|"),
		[filteredData, rowIdField]
	);
	const effectiveRowSelectionModel = useMemo(() => {
		if (!checkboxSelectionVisibleOnly) return rowSelectionModel;
		const selectedIds = Array.isArray(rowSelectionModel) ? rowSelectionModel : [];
		const currentPageSet = new Set(currentPageIds.map((id) => String(id)));
		return selectedIds.filter((id) => currentPageSet.has(String(id)));
	}, [checkboxSelectionVisibleOnly, rowSelectionModel, currentPageIds]);

	const enhancedColumns = useMemo(() => {
		return (columns || []).map((col) => {
			if (!col || col.sortComparator) return col;
			if (isDateLikeField(col.field, col.type)) {
				return { ...col, sortComparator: dateSortComparator };
			}
			return col;
		});
	}, [columns]);

	const valueOptionsColumns = useMemo(() => {
		return (enhancedColumns || []).map((col) => {
			if (!col?.field) return col;
			if (Array.isArray(col.valueOptions) || col.type !== "singleSelect") return col;
			const valueOptions = Array.from(
				new Set(
					(data || [])
						.map((row) => row?.[col.field])
						.filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
				)
			);
			if (!valueOptions.length) return col;
			return { ...col, valueOptions };
		});
	}, [enhancedColumns, data]);

	const shallowEqualModels = (a = {}, b = {}) => {
		const aKeys = Object.keys(a);
		const bKeys = Object.keys(b);
		if (aKeys.length !== bKeys.length) return false;
		for (let i = 0; i < aKeys.length; i += 1) {
			const key = aKeys[i];
			if (a[key] !== b[key]) return false;
		}
		return true;
	};

	const enforceColumnVisibilityRules = React.useCallback(
		(model = {}) => {
			const next = { ...model };
			const fields = new Set((enhancedColumns || []).map((col) => col?.field).filter(Boolean));

			// Remove stale fields from the visibility model.
			Object.keys(next).forEach((field) => {
				if (!fields.has(field)) delete next[field];
			});

			if (preventHidingLastColumn) {
				const visibleFields = (enhancedColumns || [])
					.filter((col) => col?.field && next[col.field] !== false)
					.map((col) => col.field);

				if (visibleFields.length === 0) {
					const fallbackField =
						(lockActiveColumnInManageColumns && activeMenuColumnField && fields.has(activeMenuColumnField))
							? activeMenuColumnField
							: (enhancedColumns || []).find((col) => col?.field)?.field;
					if (fallbackField) delete next[fallbackField];
				}
			}

			return next;
		},
		[enhancedColumns, lockActiveColumnInManageColumns, activeMenuColumnField, preventHidingLastColumn]
	);

	useEffect(() => {
		setPaginationModel((prev) => ({ ...prev, pageSize: normalizedPageSize }));
	}, [normalizedPageSize]);

	useEffect(() => {
		setColumnVisibilityModel((prev) => {
			const normalized = enforceColumnVisibilityRules(prev);
			return shallowEqualModels(prev, normalized) ? prev : normalized;
		});
	}, [enforceColumnVisibilityRules]);

	const stripHtml = (html = "") =>
		String(html)
			.replace(/<style[\s\S]*?<\/style>/gi, " ")
			.replace(/<script[\s\S]*?<\/script>/gi, " ")
			.replace(/<[^>]*>/g, " ")
			.replace(/&nbsp;/g, " ")
			.replace(/\s+/g, " ")
			.trim();

	const extractTextFromReactNode = (node) => {
		if (node == null || typeof node === "boolean") return "";
		if (typeof node === "string" || typeof node === "number") return String(node);
		if (Array.isArray(node)) return node.map(extractTextFromReactNode).join(" ");

		if (typeof node === "object" && node.props) {
			const html = node.props?.dangerouslySetInnerHTML?.__html;
			if (html) return stripHtml(html);

			const candidates = [
				node.props.label,
				node.props.text,
				node.props.name,
				node.props.title,
				node.props.value,
			]
				.map(extractTextFromReactNode)
				.filter(Boolean);

			if (candidates.length) return candidates.join(" ");
			return extractTextFromReactNode(node.props.children);
		}

		return "";
	};

	const callValueFormatterSafely = (col, params, value) => {
		if (typeof col.valueFormatter !== "function") return null;

		try {
			const out1 = col.valueFormatter({ ...params, value });
			if (out1 != null && out1 !== "") return String(out1);
		} catch { }
		try {
			const out2 = col.valueFormatter(value);
			if (out2 != null && out2 !== "") return String(out2);
		} catch { }

		return null;
	};

	const getDisplayedCellText = ({ col, row, getRowId }) => {
		if (!col?.field) return ""; // skip "Actions" etc.

		const field = col.field;
		const rawValue = row?.[field];

		const params = {
			id: getRowId?.(row) ?? row?.id ?? row?.[field],
			field,
			row,
			value: rawValue,
		};

		// optional explicit hook (best for complex cases)
		if (typeof col.getSearchValue === "function") {
			try {
				return String(col.getSearchValue(params) ?? "");
			} catch {
				return "";
			}
		}

		// try renderCell “display text”
		if (typeof col.renderCell === "function") {
			try {
				const rendered = col.renderCell(params);
				const text = extractTextFromReactNode(rendered);
				if (text) return text;
			} catch {
				// ignore
			}
		}

		// valueGetter fallback
		let value = rawValue;
		if (typeof col.valueGetter === "function") {
			try {
				const result = col.valueGetter(rawValue, row, col, null);
				// If it returns something meaningful, use it
				if (result != null && result !== "") {
					value = result;
				} else {
					value = col.valueGetter(params);
				}
			} catch {
				try {
					value = col.valueGetter(params);
				} catch {
					value = rawValue;
				}
			}
		}

		// valueFormatter fallback
		const formatted = callValueFormatterSafely(col, params, value);
		if (formatted != null) return formatted;
        
		if (Array.isArray(value)) {
			return value
				.map((item) => {
				if (!item) return "";
				if (typeof item === "object") {
					return [
					item.machine_name,
					item.machine_number
					]
					.map((v) => String(v || ""))
					.join(" ");
				}
				return String(item);
				})
				.join(" ");
		}

		return value == null ? "" : String(value);
	};

	const defaultFilter = (query, rows, cols, getRowId) => {
		const q = (query || "").toLowerCase().trim();
		if (!q) return rows;

		return (rows || []).filter((row) =>
			(cols || []).some((col) => {
				if (!col?.field) return false;              // skip columns without field
				if (col.searchable === false) return false; // allow opt-out
				const text = getDisplayedCellText({ col, row, getRowId }).toLowerCase();
				return text.includes(q);
			})
		);
	};



	const runSearch = (query, rows) => {
		const q = (query || "").toLowerCase();

		// ✅ Use custom handler if provided
		if (typeof handleSearch === "function") {
			try {
				const result = handleSearch({ query: q, data: rows, columns: enhancedColumns });

				// If handler returns an array, we use it
				if (Array.isArray(result)) {
					setFilteredData(result);
					return;
				}

				// If handler returns a Promise, resolve it
				if (result && typeof result.then === "function") {
					result
						.then((nextRows) => {
							if (Array.isArray(nextRows)) setFilteredData(nextRows);
						})
						.catch(() => {
							// If custom search errors, fall back to empty (or you can fall back to defaultFilter)
							setFilteredData([]);
						});
					return;
				}

				// If it returns void/anything else, assume the parent is handling side effects.
				return;
			} catch {
				setFilteredData([]);
				return;
			}
		}

		const filteredRows = defaultFilter(query, data, columns, (r) => r[rowIdField]);
		setFilteredData(filteredRows);
	};

	// Keep filteredData in sync with data changes + current search query
	useEffect(() => {
		runSearch(searchText, data || []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, searchText, enhancedColumns, handleSearch]);

	const handleSelectionChange = (newSelection) => {
		if (!onSelect) return;
		try {
			let ids = [];
			if (Array.isArray(newSelection)) {
				ids = newSelection;
			} else if (newSelection && typeof newSelection === "object") {
				if (newSelection.type === "exclude") {
					const excluded = newSelection.ids instanceof Set ? Array.from(newSelection.ids) : [];
					const excludedSet = new Set(excluded.map((id) => String(id)));
					ids = (filteredData || [])
						.map((row) => row?.[rowIdField])
						.filter((id) => !excludedSet.has(String(id)));
				} else if (newSelection.ids instanceof Set) ids = Array.from(newSelection.ids);
				else if (newSelection instanceof Set) ids = Array.from(newSelection);
			}

			if (checkboxSelectionVisibleOnly) {
					const pageIds = currentPageIds;
					const pageIdSet = new Set(pageIds.map((id) => String(id)));

				const prevIds = Array.isArray(rowSelectionModel) ? rowSelectionModel : [];
				const prevOffPage = prevIds.filter((id) => !pageIdSet.has(String(id)));
				const nextOnPage = ids.filter((id) => pageIdSet.has(String(id)));
				ids = Array.from(new Set([...prevOffPage, ...nextOnPage]));
			}

			onSelect(ids);
		} catch {
			onSelect([]);
		}
	};

	const handleSearchChange = (e) => {
		const query = e.target.value.toLowerCase();
		setSearchText(query);
		// filtering happens via useEffect/runSearch
	};

	const shouldUseCustomColumnMenu =
		hideFilterFromColumnMenu || lockActiveColumnInManageColumns || preventHidingLastColumn;

	const ColumnMenuWithRules = React.useMemo(() => {
		if (!shouldUseCustomColumnMenu) return null;

		const isActionsColumn = (column = {}) => {
			const field = String(column?.field || "").trim().toLowerCase();
			const type = String(column?.type || "").trim().toLowerCase();
			const header = String(column?.headerName || "").trim().toLowerCase();
			return field === "actions" || type === "actions" || header === "actions";
		};

		const isUtilityColumn = (column = {}) => {
			const field = String(column?.field || "").trim().toLowerCase();
			// MUI internal columns (checkbox/detail/reorder/etc.)
			return field.startsWith("__");
	};

		const CustomColumnMenuColumnsItem = (props) => {
			const { colDef, onClick } = props;
			const apiRef = useGridApiContext();
			const rootProps = useGridRootProps();
			const visibleColumns = apiRef.current.getVisibleColumns() || [];
			const visibleDataColumns = visibleColumns.filter(
				(col) => col?.field && col?.hideable !== false && !isActionsColumn(col) && !isUtilityColumn(col)
			);
			const isOnlyOneDataColumnLeft = visibleDataColumns.length <= 1;
			const isHideDisabled = colDef?.hideable === false || (preventHidingLastColumn && isOnlyOneDataColumnLeft);

			const handleHideColumn = (event) => {
				if (isHideDisabled || !colDef?.field) return;
				apiRef.current.setColumnVisibility(colDef.field, false);
				onClick(event);
			};

			const handleManageColumns = (event) => {
				onClick(event);
				apiRef.current.showPreferences("columns");
			};

			if (rootProps.disableColumnSelector) {
				return null;
			}

			return (
				<>
					<MenuItem onClick={handleHideColumn} disabled={isHideDisabled}>
						<ListItemIcon>
							<rootProps.slots.columnMenuHideIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>{apiRef.current.getLocaleText("columnMenuHideColumn")}</ListItemText>
					</MenuItem>
					<MenuItem onClick={handleManageColumns}>
						<ListItemIcon>
							<rootProps.slots.columnMenuManageColumnsIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>{apiRef.current.getLocaleText("columnMenuManageColumns")}</ListItemText>
					</MenuItem>
				</>
			);
		};

		const CustomColumnMenu = (props) => {
			useEffect(() => {
				if (lockActiveColumnInManageColumns) {
					setActiveMenuColumnField(props?.colDef?.field || null);
				}
			}, [props?.colDef?.field]);

			const menuSlots = {
				...(hideFilterFromColumnMenu && { columnMenuFilterItem: null }),
				...(preventHidingLastColumn && { columnMenuColumnsItem: CustomColumnMenuColumnsItem }),
			};
			return <GridColumnMenu {...props} slots={menuSlots} />;
		};

		return CustomColumnMenu;
	}, [shouldUseCustomColumnMenu, hideFilterFromColumnMenu, lockActiveColumnInManageColumns, preventHidingLastColumn]);

	const getTogglableColumns = React.useCallback(
		(currentColumns = []) => {
			if (!lockActiveColumnInManageColumns || !activeMenuColumnField) {
				return currentColumns.map((col) => col.field);
			}
			return currentColumns
				.filter((col) => col?.field && col.field !== activeMenuColumnField)
				.map((col) => col.field);
		},
		[lockActiveColumnInManageColumns, activeMenuColumnField]
	);

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				height: "fit-content",
				width: "100%",
				minWidth: 0,
				marginBottom: "30px",
			}}
		>
			{showSearchBar && (
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: "5px",
					}}
				>
					<div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0, flexWrap: "wrap" }}>
						<TextField
							variant="outlined"
							size="small"
							value={searchText}
							onChange={handleSearchChange}
							placeholder={t("PLACEHOLDER.SEARCH")}
							style={{ minWidth: "220px" }}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<SearchIcon />
									</InputAdornment>
								),
							}}
						/>
						{extraFilters}
					</div>

					<Toolbar style={{ display: "flex", gap: "8px", margin: 0, padding: 0 }} data-tour="admin-user-actions">
						{customButtons &&
							customButtons.map((button, index) => (
								<Button
									key={index}
									type="button"
									startIcon={button?.icon}
									variant="contained"
									color={button?.color ? button?.color : "primary"}
									onClick={button.onClick}
									disabled={Boolean(button?.disabled)}
								>
									{button.label}{" "}
									{button?.upload && (
										<input
											type="file"
											hidden
											onChange={button?.handleFileChange}
											accept={button?.acceptedFile}
										/>
									)}
								</Button>
							))}
					</Toolbar>
				</div>
			)}

			<div
				style={{
					flexGrow: 1,
					width: "100%",
					maxWidth: "100%",
					minWidth: 0,
					overflowX: "auto",
					height: autoHeight
						? "inherit"
						: typeof gridHeight === "number"
							? `${gridHeight}px`
							: gridHeight || "420px",
				}}
			>
					<DataGrid
					key={rowIdSetKey}
					rows={filteredData}
					//columns={enhancedColumns}
					columns={valueOptionsColumns}
					columnVisibilityModel={columnVisibilityModel}
					pagination
					pageSizeOptions={normalizedPageSizeOptions}
						initialState={{
						pagination: {
							paginationModel: { pageSize: normalizedPageSize, page: 0 },
						},
						sorting: { sortModel: sortBy },
						}}
						localeText={
							footerSelectedRowText
								? {
									footerRowSelected: () => footerSelectedRowText,
								}
								: undefined
						}
					paginationModel={paginationModel}
					onPaginationModelChange={(model) => {
						setPaginationModel(model);
						if (typeof onPageChange === "function") onPageChange(model?.page ?? 0);
					}}
					{...(rowClick && { onRowClick })}
						rowSelectionModel={effectiveRowSelectionModel}
					keepNonExistentRowsSelected={keepNonExistentSelected}
					onRowSelectionModelChange={handleSelectionChange}
					onPageChange={onPageChange}
						autoHeight={autoHeight}
						hideFooterSelectedRowCount={hideFooterSelectedRowCount}
						disableRowSelectionOnClick
					checkboxSelection={checkboxSelection}
					checkboxSelectionVisibleOnly={checkboxSelectionVisibleOnly}
					getRowId={(row) => row[rowIdField]}
					{...(shouldUseCustomColumnMenu && {
						slots: { columnMenu: ColumnMenuWithRules },
						slotProps: {
							...(lockActiveColumnInManageColumns && {
								columnsManagement: { getTogglableColumns },
							}),
						},
					})}
					onColumnVisibilityModelChange={(newModel) => {
						const normalized = enforceColumnVisibilityRules(newModel);
						setColumnVisibilityModel((prev) => (shallowEqualModels(prev, normalized) ? prev : normalized));
					}}
					sx={{
						width: "100%",
						minWidth: 0,
						borderRadius: "10px",
						backgroundColor: (theme) => theme.palette.background.paper,
						"& .MuiDataGrid-main": { backgroundColor: (theme) => theme.palette.background.paper },
						"& .MuiDataGrid-virtualScroller": { overflowX: "auto !important" },
						"& .MuiDataGrid-cell": { backgroundColor: (theme) => theme.palette.background.paper },
						"& .MuiDataGrid-columnHeaders": { backgroundColor: (theme) => theme.palette.background.paper },
						"& .MuiDataGrid-columnHeader": { backgroundColor: (theme) => theme.palette.background.paper },
						"& .MuiDataGrid-footerContainer": { backgroundColor: (theme) => theme.palette.background.paper },
						"& .MuiDataGrid-root": { backgroundColor: (theme) => theme.palette.background.paper },
						".MuiTablePagination-selectLabel": { marginBottom: 0 },
						".MuiTablePagination-displayedRows": { marginBottom: 0 },
						...(disableSelectAll && {
							"& .MuiDataGrid-columnHeaderCheckbox .MuiButtonBase-root": { display: "none" },
							"& .MuiDataGrid-columnHeaderCheckbox": { pointerEvents: "none" },
						}),
					}}
				/>
			</div>
		</div>
	);
};

export default Datagrid;
